/**
 * HistoryList — the /history screen (F4.T6, RF11).
 *
 * Lists sessions newest-first with a search box (q), a target-language filter,
 * and a date range (from/to). Supports inline rename and delete-with-confirm.
 * Clicking a row navigates to /history/:id (SessionDetail).
 *
 * Debounces the query inputs so typing doesn't hammer GET /sessions.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/authStore';
import { historyApi, type HistoryQuery } from '@/features/history/historyApi';
import type { Session, SessionStatus } from '@/features/session/sessionApi';

const LANG_FILTERS = [
  { value: '', label: 'Todos os idiomas' },
  { value: 'en-US', label: 'Inglês (EUA)' },
  { value: 'es-ES', label: 'Espanhol (Espanha)' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'fr-FR', label: 'Francês' },
  { value: 'de-DE', label: 'Alemão' },
];

const STATUS_LABEL: Record<SessionStatus, string> = {
  live: 'Ao vivo',
  ended: 'Encerrada',
  error: 'Erro',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
}

export function HistoryList(): ReactElement {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);

  const [q, setQ] = useState('');
  const [lang, setLang] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline rename state.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  // Debounce the filter set into a single query object.
  const [query, setQuery] = useState<HistoryQuery>({});
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next: HistoryQuery = {};
      if (q.trim()) next.q = q.trim();
      if (lang) next.lang = lang;
      if (from) next.from = from;
      if (to) next.to = to;
      setQuery(next);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [q, lang, from, to]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setSessions(await historyApi.list(query));
    } catch {
      setError('Não foi possível carregar o histórico.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  // Client-side newest-first guard in case the backend order changes.
  const ordered = useMemo(
    () =>
      [...sessions].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [sessions],
  );

  function startRename(session: Session): void {
    setEditingId(session.id);
    setDraftName(session.name);
  }

  async function commitRename(id: string): Promise<void> {
    const name = draftName.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    setBusyId(id);
    try {
      const updated = await historyApi.rename(id, name);
      setSessions((prev) => prev.map((s) => (s.id === id ? updated : s)));
      setEditingId(null);
    } catch {
      setError('Não foi possível renomear a sessão.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(session: Session): Promise<void> {
    const ok = window.confirm(
      `Excluir a sessão "${session.name}"? Esta ação é permanente.`,
    );
    if (!ok) return;
    setBusyId(session.id);
    try {
      await historyApi.remove(session.id);
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
    } catch {
      setError('Não foi possível excluir a sessão.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.link}>
          Console
        </Link>
        <strong style={styles.brand}>Histórico</strong>
        <Link to="/credentials" style={styles.link}>
          Credenciais
        </Link>
        <button type="button" style={styles.logout} onClick={logout}>
          Sair
        </button>
      </nav>

      <section style={styles.body}>
        <div style={styles.filters}>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar na transcrição ou nome…"
            style={{ ...styles.input, flex: 2, minWidth: 220 }}
            aria-label="Buscar"
          />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            style={styles.input}
            aria-label="Idioma de destino"
          >
            {LANG_FILTERS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <label style={styles.dateField}>
            <span style={styles.dateLabel}>De</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={styles.input}
              aria-label="Data inicial"
            />
          </label>
          <label style={styles.dateField}>
            <span style={styles.dateLabel}>Até</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={styles.input}
              aria-label="Data final"
            />
          </label>
        </div>

        {error && <p style={styles.error}>{error}</p>}

        {loading ? (
          <p style={styles.muted}>Carregando…</p>
        ) : ordered.length === 0 ? (
          <p style={styles.muted}>Nenhuma sessão encontrada.</p>
        ) : (
          <ul style={styles.list}>
            {ordered.map((session) => {
              const isEditing = editingId === session.id;
              const isBusy = busyId === session.id;
              return (
                <li
                  key={session.id}
                  style={styles.row}
                  onClick={() => {
                    if (!isEditing) navigate(`/history/${session.id}`);
                  }}
                >
                  <div style={styles.rowMain}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={draftName}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setDraftName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void commitRename(session.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        style={{ ...styles.input, maxWidth: 320 }}
                        aria-label="Novo nome"
                      />
                    ) : (
                      <strong style={styles.rowName}>{session.name}</strong>
                    )}
                    <div style={styles.metaLine}>
                      {(session.source_lang_mode === 'auto'
                        ? 'auto'
                        : (session.source_lang ?? '—'))}{' '}
                      → {session.target_lang} · {formatDate(session.created_at)}
                    </div>
                  </div>

                  <span style={styles.statusPill}>
                    {STATUS_LABEL[session.status]}
                  </span>

                  <div
                    style={styles.actions}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          style={styles.smallPrimary}
                          disabled={isBusy}
                          onClick={() => void commitRename(session.id)}
                        >
                          Salvar
                        </button>
                        <button
                          type="button"
                          style={styles.smallGhost}
                          onClick={() => setEditingId(null)}
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          style={styles.smallGhost}
                          disabled={isBusy}
                          onClick={() => startRename(session)}
                        >
                          Renomear
                        </button>
                        <button
                          type="button"
                          style={styles.smallDanger}
                          disabled={isBusy}
                          onClick={() => void handleDelete(session)}
                        >
                          Excluir
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--rt-bg)', color: 'var(--rt-text)' },
  nav: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    padding: '0.75rem 1.5rem',
    borderBottom: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
  },
  brand: { fontSize: '0.95rem', marginRight: 'auto' },
  link: { color: 'var(--rt-primary)', textDecoration: 'none', fontSize: '0.9rem' },
  logout: {
    border: '1px solid var(--rt-border)',
    background: 'transparent',
    color: 'var(--rt-text)',
    borderRadius: 8,
    padding: '0.35rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  body: {
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: 960,
    margin: '0 auto',
  },
  filters: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  input: {
    padding: '0.55rem 0.6rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
    color: 'var(--rt-text)',
    fontSize: '0.9rem',
  },
  dateField: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  dateLabel: { fontSize: '0.75rem', color: 'var(--rt-text-muted)' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.85rem 1rem',
    borderRadius: 10,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
    cursor: 'pointer',
  },
  rowMain: { display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1, minWidth: 0 },
  rowName: { fontSize: '0.95rem' },
  metaLine: { fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
  statusPill: {
    fontSize: '0.75rem',
    padding: '0.2rem 0.5rem',
    borderRadius: 999,
    border: '1px solid var(--rt-border)',
    whiteSpace: 'nowrap',
    color: 'var(--rt-text-muted)',
  },
  actions: { display: 'flex', gap: '0.4rem' },
  smallPrimary: {
    padding: '0.4rem 0.7rem',
    borderRadius: 8,
    border: 'none',
    background: 'var(--rt-primary)',
    color: 'var(--rt-primary-text)',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  smallGhost: {
    padding: '0.4rem 0.7rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'transparent',
    color: 'var(--rt-text)',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  smallDanger: {
    padding: '0.4rem 0.7rem',
    borderRadius: 8,
    border: '1px solid var(--rt-danger)',
    background: 'transparent',
    color: 'var(--rt-danger)',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  muted: { color: 'var(--rt-text-muted)' },
  error: { color: 'var(--rt-danger)', fontSize: '0.85rem', margin: 0 },
};
